import {
  getObject,
  getLeoTimestamp,
  LeoTimestamp,
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { ProjectName, ProjectNameDTO } from "../types/projectName";
/* eslint-disable import/export */
export abstract class CreateNewProjectRPC implements
  LeoRPC<CreateNewProjectRPC.Request, CreateNewProjectRPC.Response, CreateNewProjectRPC.Errors.Errors>
{
  abstract execute(
    request: CreateNewProjectRPC.Request
  ): Promise<LeoRPCResult<CreateNewProjectRPC.Response, CreateNewProjectRPC.Errors.Errors>>;
}

export namespace CreateNewProjectRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectName: ProjectNameDTO;
    startDate: string;
    endDate: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isCreateNewProjectRPCRequest: boolean = true;
  
    readonly projectName: ProjectName;
    readonly startDate: LeoTimestamp;
    readonly endDate: LeoTimestamp;
  
  
    constructor(
    projectName: ProjectName, 
    startDate: LeoTimestamp, 
    endDate: LeoTimestamp
    ) {
      this.projectName = projectName;
      this.startDate = startDate;
      this.endDate = endDate;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectName = ProjectName.fromDTO(getObject(dto, "projectName", InvalidRequestProjectNameError));
      const startDate = getLeoTimestamp(dto, "startDate", InvalidRequestStartDateError);
      const endDate = getLeoTimestamp(dto, "endDate", InvalidRequestEndDateError);
      return new Request(
        projectName,
        startDate,
        endDate
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectName: this.projectName.toDTO(),
          startDate: this.startDate.toDTO(),
          endDate: this.endDate.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `CreateNewProjectRPC(projectName='${this.projectName}',startDate='${this.startDate}',endDate='${this.endDate}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    projectId: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isCreateNewProjectResponse: boolean = true;
  
    readonly projectId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID
    ) {
      this.projectId = projectId;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const projectId = getLeoUuid(dto, "projectId", InvalidResponseProjectIdError);
      return new Response(
        projectId
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `CreateNewProject(projectId='${this.projectId}')`;
  };
  

  export enum RPCError {
    ProjectNameNotUnique = "PROJECT_NAME_NOT_UNIQUE",
    InvalidProjectName = "INVALID_PROJECT_NAME",
    InvalidProjectDates = "INVALID_PROJECT_DATES"
  }

  export namespace Errors {
    
    export interface ProjectNameNotUniqueDTO {
      code: string;
    }
    
    export class ProjectNameNotUnique extends LeoRPCError<RPCError> {
      readonly isProjectNameNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectNameNotUnique;
        super(code);
      }
    
    
      toDTO(): ProjectNameNotUniqueDTO {
        const dto: ProjectNameNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectNameNotUnique.prototype.toString = function toString(): string {
      return `CreateNewProjectRPC.PROJECT_NAME_NOT_UNIQUE()`;
    };
    
    
    export interface InvalidProjectNameDTO {
      code: string;
    }
    
    export class InvalidProjectName extends LeoRPCError<RPCError> {
      readonly isInvalidProjectName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectName;
        super(code);
      }
    
    
      toDTO(): InvalidProjectNameDTO {
        const dto: InvalidProjectNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectName.prototype.toString = function toString(): string {
      return `CreateNewProjectRPC.INVALID_PROJECT_NAME()`;
    };
    
    
    export interface InvalidProjectDatesDTO {
      code: string;
    }
    
    export class InvalidProjectDates extends LeoRPCError<RPCError> {
      readonly isInvalidProjectDates: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectDates;
        super(code);
      }
    
    
      toDTO(): InvalidProjectDatesDTO {
        const dto: InvalidProjectDatesDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectDates.prototype.toString = function toString(): string {
      return `CreateNewProjectRPC.INVALID_PROJECT_DATES()`;
    };
    

    export type Errors = ProjectNameNotUnique | InvalidProjectName | InvalidProjectDates;
    export type ErrorsDTO = ProjectNameNotUniqueDTO | InvalidProjectNameDTO | InvalidProjectDatesDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.ProjectNameNotUnique:
          return new ProjectNameNotUnique();
        case RPCError.InvalidProjectName:
          return new InvalidProjectName();
        case RPCError.InvalidProjectDates:
          return new InvalidProjectDates();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectNameError extends CreateNewProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectNameError";
  }
}

export class InvalidRequestStartDateError extends CreateNewProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestStartDateError";
  }
}

export class InvalidRequestEndDateError extends CreateNewProjectRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEndDateError";
  }
}

export class InvalidResponseProjectIdError extends CreateNewProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectIdError";
  }
}

export class InvalidResponseProjectNameNotUniqueError extends CreateNewProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectNameNotUniqueError";
  }
}

export class InvalidResponseInvalidProjectNameError extends CreateNewProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectNameError";
  }
}

export class InvalidResponseInvalidProjectDatesError extends CreateNewProjectRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectDatesError";
  }
}

